import type { MapMarker } from '@gpx-spa/geo-domain'
import L from 'leaflet'
import {
  circleMarkerDivHtml,
  resolveCircleMarkerOuterSizePx,
} from '../shared/circle-marker-element.js'
import {
  mapMarkerIconDivHtml,
  resolveMapMarkerIconLayout,
} from '../shared/icon-marker-element.js'
import { PIN_ICON_HEIGHT, PIN_ICON_WIDTH, pinIconDataUrl } from '../shared/pin-marker-element.js'
import { escapeHtml } from './leaflet-geojson-helpers.js'

export function bindPopup(lm: L.Marker, m: MapMarker, skip = false): void {
  if (skip) return
  const fallback =
    m.style.kind === 'circle' && m.style.label?.trim()
      ? m.style.label.trim()
      : ''
  const popupText = m.popup ?? fallback
  if (!popupText) return
  const html = m.popupHtml === true ? popupText : `<div>${escapeHtml(popupText)}</div>`
  lm.bindPopup(m.popupHtml === true ? popupText : html)
}

export type LeafletMapMarkerOptions = {
  staticPopup?: boolean
  pane?: string
}

function markerOptions(pane: string | undefined): L.MarkerOptions {
  return {
    interactive: true,
    zIndexOffset: 800,
    ...(pane ? { pane } : {}),
  }
}

function leafletImageIconMarker(m: MapMarker, opts: LeafletMapMarkerOptions): L.Marker {
  const staticPopup = opts.staticPopup !== false
  if (m.style.kind !== 'icon') {
    throw new Error('leafletImageIconMarker: expected icon style')
  }
  const ic = m.style.icon
  if (ic.shadowUrl) {
    const icon = L.icon({
      iconUrl: ic.url,
      iconSize: (ic.size ?? [28, 36]) as L.PointExpression,
      iconAnchor: (ic.anchor ?? [14, 36]) as L.PointExpression,
      shadowUrl: ic.shadowUrl,
      shadowSize: ic.shadowSize as L.PointExpression | undefined,
      shadowAnchor: ic.shadowAnchor as L.PointExpression | undefined,
      popupAnchor: (ic.popupAnchor ?? [0, -31]) as L.PointExpression,
    })
    const marker = L.marker(L.latLng(m.position.lat, m.position.lng), {
      ...markerOptions(opts.pane),
      icon,
    })
    bindPopup(marker, m, !staticPopup)
    return marker
  }
  const layout = resolveMapMarkerIconLayout(ic)
  const marker = L.marker(L.latLng(m.position.lat, m.position.lng), {
    ...markerOptions(opts.pane),
    zIndexOffset: 1200,
    icon: L.divIcon({
      className: 'gpx-leaflet-image-marker',
      html: mapMarkerIconDivHtml(ic),
      iconSize: layout.size,
      iconAnchor: layout.anchor,
      popupAnchor: layout.popupAnchor,
    }),
  })
  bindPopup(marker, m, !staticPopup)
  return marker
}

function leafletCircleMarker(m: MapMarker, opts: LeafletMapMarkerOptions): L.Marker {
  const staticPopup = opts.staticPopup !== false
  if (m.style.kind !== 'circle') {
    throw new Error('leafletCircleMarker: expected circle style')
  }
  const st = m.style
  const outer = resolveCircleMarkerOuterSizePx(st)
  const half = outer / 2
  const marker = L.marker(L.latLng(m.position.lat, m.position.lng), {
    ...markerOptions(opts.pane),
    icon: L.divIcon({
      className: 'gpx-leaflet-circle-marker',
      html: circleMarkerDivHtml(st),
      iconSize: [outer, outer],
      iconAnchor: [half, half],
    }),
  })
  bindPopup(marker, m, !staticPopup)
  return marker
}

function leafletPinMarker(m: MapMarker, opts: LeafletMapMarkerOptions): L.Marker {
  const staticPopup = opts.staticPopup !== false
  if (m.style.kind !== 'pin') {
    throw new Error('leafletPinMarker: expected pin style')
  }
  const color = m.style.color
  const base = markerOptions(opts.pane)
  const marker =
    color === undefined
      ? L.marker(L.latLng(m.position.lat, m.position.lng), base)
      : L.marker(L.latLng(m.position.lat, m.position.lng), {
          ...base,
          icon: L.icon({
            iconUrl: pinIconDataUrl(color),
            iconSize: [PIN_ICON_WIDTH, PIN_ICON_HEIGHT],
            iconAnchor: [Math.round(PIN_ICON_WIDTH / 2), PIN_ICON_HEIGHT],
            popupAnchor: [1, -34],
          }),
        })
  bindPopup(marker, m, !staticPopup)
  return marker
}

export function leafletMarkerFromMapMarker(m: MapMarker, opts?: LeafletMapMarkerOptions): L.Marker {
  const options: LeafletMapMarkerOptions = opts ?? {}
  switch (m.style.kind) {
    case 'icon':
      return leafletImageIconMarker(m, options)
    case 'circle':
      return leafletCircleMarker(m, options)
    case 'pin':
      return leafletPinMarker(m, options)
    default: {
      const _exhaustive: never = m.style
      return _exhaustive
    }
  }
}
